/**
 * 상조 랜딩(/match/request?template=sangjo) 콘텐츠 데이터.
 * design_handoff_sangjo_landing README(hifi 스펙) 기준. 금액·수치는 기획 확정값.
 */
import { EventTriggerAndTargetType } from "goi_common";
import { LeadUrgencyStatus } from "@/apis/postLeadV2";

export const SANGJO_LANDING_EVENT_PATH = "sangjo_lp";
export type SangjoLandingTrigger = EventTriggerAndTargetType["sangjo_lp"];

/** 그래픽 아이콘(public/sangjo-landing/*.svg). 색·비율 변경 금지(DS 원본). */
export type SangjoGraphicIcon =
  | "receipt"
  | "shield"
  | "building"
  | "shopping-bag"
  | "helper"
  | "price-list"
  | "calculator"
  | "100won";

/** 히어로 예상 견적서 카드 — 공제 항목 */
export const HERO_DEDUCT_ROWS = [
  { label: "음식 도우미 2명 제외", value: "−24만원" },
  { label: "수의 제외", value: "−20만원" },
  { label: "남자 상복 2벌 제외", value: "−10만원" },
];

/** 상조 안내 — 장례식장에 내는 비용 */
export const GUIDE_PARLOR_ITEMS: { icon: SangjoGraphicIcon; title: string; desc: string }[] = [
  { icon: "building", title: "시설 사용료", desc: "빈소, 안치실 등" },
  { icon: "shopping-bag", title: "음식 · 제단꽃", desc: "조문객 식사, 제단" },
];

/** 상조 안내 — 포인트 3개 */
export const GUIDE_POINTS: { tone: "warn" | "good"; title: string; desc: string }[] = [
  {
    tone: "warn",
    title: "장례식장에서 한다고 꼭 싸지는 않아요",
    desc: "품목별 가격을 비교하기 어렵고, 경황없이 선택하다 보면 예상보다 많은 비용을 낼 수 있어요.",
  },
  {
    tone: "warn",
    title: "저가 상조는 추가금이 붙는 경우가 많아요",
    desc: "처음 안내된 상품가에 필요한 품목이 빠져 있을 수 있어요.",
  },
  {
    tone: "good",
    title: "고이는 미리 안내한 그대로, 쓴 만큼만 나와요",
    desc: "필요한 품목과 예상 금액을 먼저 확인하고 선택해요.",
  },
];

/** 차별성 리스트 */
export const DIFF_ITEMS: { icon: SangjoGraphicIcon; title: string; desc: string }[] = [
  {
    icon: "price-list",
    title: "장례 전, 견적부터 꼼꼼하게 안내",
    desc: "어떤 항목에 얼마가 드는지 미리 확인해요.",
  },
  {
    icon: "calculator",
    title: "실제로 쓴 만큼만 정산",
    desc: "쓰지 않은 품목은 비용에서 빼드려요.",
  },
  {
    icon: "shield",
    title: "비싼 품목 권유, 노잣돈 요구 없음",
    desc: "필요 이상의 상품을 권하지 않아요.",
  },
];

/** 신뢰 수치 */
export const TRUST_STATS = [
  { value: "6,500건+", label: "누적 장례", isAccent: false },
  { value: "3,000건+", label: "고객 후기", isAccent: true },
  { value: "4.97점", label: "만족도", isAccent: false },
];
export const TRUST_STATS_BASIS = "2026년 10월 기준";

/** 후기 3개. 원문은 ssr-platform /review/[id]/ 라 <a href> 전체 이동. */
export const TRUST_REVIEWS = [
  {
    id: 3012,
    author: "윤OO님",
    date: "2026.10.21",
    text: "상조 비용에서도 사용하지 않은 비용도 정확하게 공제하고 정산하는 것을 보니 정말 투명한 고이상조라는 생각을 했습니다. 큰누님과 큰매형도 금액을 보고 깜짝 놀라셨습니다.",
  },
  {
    id: 2701,
    author: "정OO님",
    date: "2026.09.04",
    text: "정말로... 만족스러웠습니다. 남편도 너무 감사해 했고, 덕분에 아버님을 잘 모시고 보내드릴 수 있었습니다. 비용적인 부분도 생각했던 것보다 굉장히 합리적이었습니다.",
  },
  {
    id: 2529,
    author: "조OO님",
    date: "2026.08.12",
    text: "주변의 빈소에서 본 다른 장례지도사 분들과 구별될만큼 품격이 느껴지는 지도사님이셨습니다. 장례식을 하게 될 일이 발생하면 무조건 고이상조를 추천 및 의뢰할 것입니다.",
  },
];

/**
 * 조문객 수 선택지 + 예상 상조 서비스 비용.
 * condolers 는 견적 API 로 넘기는 기존 값(조문객예상인원_LIST)과 동일해야 한다.
 */
export const GUEST_OPTIONS: {
  condolers: string;
  label: string;
  desc: string;
  estimate: { price: string; basePrice?: string; note: string; isDeductNote: boolean };
}[] = [
  {
    condolers: "0명(무빈소)",
    label: "0명",
    desc: "무빈소",
    estimate: { price: "159만원", note: "무빈소 기준", isDeductNote: false },
  },
  {
    condolers: "50명 내외",
    label: "50명",
    desc: "내외",
    estimate: {
      price: "265만원",
      basePrice: "289만원",
      note: "음식 도우미 2명 공제 −24만원",
      isDeductNote: true,
    },
  },
  {
    condolers: "100명 내외",
    label: "100명",
    desc: "내외",
    estimate: { price: "289만원", note: "기본 구성 기준", isDeductNote: false },
  },
  {
    condolers: "200명 이상",
    label: "200명",
    desc: "이상",
    estimate: { price: "289만원~", note: "음식 도우미, 남녀 상복 추가 예상", isDeductNote: false },
  },
];

/** 현재 상황(장례준비긴급도_LIST 원문) → v2 리드 status */
export const URGENCY_TO_LEAD_STATUS: Record<string, LeadUrgencyStatus> = {
  "급하지 않지만 미리 알아보려고 해요.": "여유",
  "1주에서 한 달 정도 기간이 남은 것 같아요.": "1달",
  "임종이 며칠 남지 않았어요.": "임박",
  "임종하신 상태입니다.": "임종상태",
};

/** 긴급 전화 박스를 띄우는 현재 상황 */
export const URGENT_STATUS = "임종하신 상태입니다.";

/** 상담원이 보는 견적 memo / 리드 slack 안내 문구 */
export const OPS_GUIDE_MESSAGE =
  "상조 관련 검색을 한 고객이에요. 고이에 대해 적극적인 세일즈 및 타상조 깨기 진행 부탁드립니다.";

export const PRIVACY_CONSENT_URL =
  "https://goifuneral.notion.site/146a79013e6c40a9b4731dd07a8ad99d";
export const NIGHT_CONSENT_URL = "https://goifuneral.notion.site/3e893cfdb8584fb28a4086a60aef5ad4";

export const GOI_PHONE = "1666-9784";

/** 이탈방지 모달 → 고이프라임(100원 상조) */
export const PRIME_EXIT_PATH = "/?utm_campaign=sangjo_landing_exit";

/** 이탈방지 모달 세션당 1회 노출 마킹 */
export const EXIT_MODAL_SHOWN_KEY = "sangjo_landing_exit_shown";
